import { useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { CheckCircle, AlertTriangle, Clock, CreditCard, ExternalLink, Loader2 } from 'lucide-react'
import { useOrganization } from '../hooks/useOrganization'
import { useAppStore } from '../stores/app'
import { createCheckoutSession, createBillingPortalSession } from '../lib/api'
import type { SubscriptionStatus } from '../lib/api'
import { useState } from 'react'

interface Plan {
  id: string
  name: string
  price: string
  priceDetail: string
  credits: string
  maxUsers: string
  features: string[]
  lookupKey: string | null
  isFree: boolean
}

const PLANS: Plan[] = [
  {
    id: 'starter',
    name: 'Starter',
    price: 'Gratis',
    priceDetail: '',
    credits: '50 creditos/mes',
    maxUsers: 'Hasta 2 usuarios',
    features: ['Generacion de contenido', 'Visual basico', 'Chat con IA'],
    lookupKey: null,
    isFree: true,
  },
  {
    id: 'professional',
    name: 'Professional',
    price: '$99',
    priceDetail: '/mes USD',
    credits: '500 creditos/mes',
    maxUsers: 'Hasta 10 usuarios',
    features: ['Todo lo de Starter', 'Generacion de imagenes avanzada', 'Soporte prioritario', 'Configuracion de agentes'],
    lookupKey: 'professional_monthly',
    isFree: false,
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: '$299',
    priceDetail: '/mes USD',
    credits: 'Creditos ilimitados',
    maxUsers: 'Usuarios ilimitados',
    features: ['Todo lo de Professional', 'SLA garantizado', 'Onboarding dedicado', 'API access', 'Reportes avanzados'],
    lookupKey: 'enterprise_monthly',
    isFree: false,
  },
]

function statusConfig(status: SubscriptionStatus): { label: string; className: string; icon: React.ReactNode } {
  switch (status) {
    case 'active':
      return { label: 'Activa', className: 'bg-emerald-100 text-emerald-700', icon: <CheckCircle className="w-3.5 h-3.5" /> }
    case 'trialing':
      return { label: 'Prueba', className: 'bg-sky-100 text-sky-700', icon: <Clock className="w-3.5 h-3.5" /> }
    case 'past_due':
      return { label: 'Pago pendiente', className: 'bg-amber-100 text-amber-700', icon: <AlertTriangle className="w-3.5 h-3.5" /> }
    case 'incomplete':
    case 'incomplete_expired':
      return { label: 'Incompleta', className: 'bg-orange-100 text-orange-700', icon: <AlertTriangle className="w-3.5 h-3.5" /> }
    case 'canceled':
      return { label: 'Cancelada', className: 'bg-red-100 text-red-700', icon: <AlertTriangle className="w-3.5 h-3.5" /> }
    case 'unpaid':
      return { label: 'Sin pago', className: 'bg-red-100 text-red-700', icon: <AlertTriangle className="w-3.5 h-3.5" /> }
    case 'paused':
      return { label: 'Pausada', className: 'bg-slate-100 text-slate-600', icon: <Clock className="w-3.5 h-3.5" /> }
    default:
      return { label: status, className: 'bg-slate-100 text-slate-600', icon: null }
  }
}

export function BillingPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { org, isLoading, isPlanActive, daysLeftInTrial } = useOrganization()
  const currentUser = useAppStore((state) => state.currentUser)
  const isAdmin = currentUser?.role === 'admin'
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null)
  const [portalLoading, setPortalLoading] = useState(false)

  useEffect(() => {
    const sessionId = searchParams.get('session_id')
    if (sessionId) {
      toast.success('Suscripcion activada correctamente')
      queryClient.invalidateQueries({ queryKey: ['organization'] })
      navigate('/billing', { replace: true })
    }
  }, [searchParams, queryClient, navigate])

  const handleUpgrade = async (lookupKey: string, planId: string) => {
    setCheckoutLoading(planId)
    try {
      const { url } = await createCheckoutSession(lookupKey)
      window.location.href = url
    } catch {
      toast.error('Error al iniciar el proceso de pago')
      setCheckoutLoading(null)
    }
  }

  const handlePortal = async () => {
    setPortalLoading(true)
    try {
      const { url } = await createBillingPortalSession()
      window.location.href = url
    } catch {
      toast.error('Error al abrir el portal de facturacion')
      setPortalLoading(false)
    }
  }

  const currentPlanId = org?.plan_id ?? 'starter'

  if (isLoading) {
    return (
      <div className="p-8 flex justify-center items-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    )
  }

  const status = org?.subscription_status ?? 'trialing'
  const { label: statusLabel, className: statusClass, icon: statusIcon } = statusConfig(status)

  return (
    <div className="p-8 max-w-5xl">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-800">Facturacion</h1>
        <p className="text-slate-500 mt-1">Gestiona tu plan y metodo de pago</p>
      </div>

      {/* Current status */}
      <div className="card mb-8">
        <h2 className="text-base font-semibold text-slate-800 mb-4">Estado actual</h2>
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Plan</p>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-semibold bg-slate-100 text-slate-800 capitalize">
              {currentPlanId}
            </span>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Estado</p>
            <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-sm font-medium ${statusClass}`}>
              {statusIcon}
              {statusLabel}
            </span>
          </div>
          {status === 'trialing' && daysLeftInTrial !== null && (
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Prueba</p>
              <span className="inline-flex items-center gap-1 text-sm font-medium text-sky-700">
                <Clock className="w-3.5 h-3.5" />
                {daysLeftInTrial} {daysLeftInTrial === 1 ? 'dia restante' : 'dias restantes'}
              </span>
            </div>
          )}
          {!isPlanActive && (
            <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 rounded-lg text-amber-700 text-sm">
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>Tu suscripcion no esta activa. Actualiza tu plan para continuar usando el servicio.</span>
            </div>
          )}
        </div>

        {isAdmin && org?.stripe_customer_id && (
          <div className="mt-5 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={handlePortal}
              disabled={portalLoading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 text-white text-sm font-medium rounded-lg hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {portalLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CreditCard className="w-4 h-4" />
              )}
              Gestionar facturacion y facturas
              {!portalLoading && <ExternalLink className="w-3.5 h-3.5" />}
            </button>
            <p className="text-xs text-slate-400 mt-2">Abre el portal de Stripe para gestionar tu metodo de pago, ver facturas y cancelar.</p>
          </div>
        )}

        {!isAdmin && (
          <p className="mt-4 text-sm text-slate-500">Solo los administradores pueden cambiar el plan o gestionar la facturacion.</p>
        )}
      </div>

      {/* Plan cards */}
      <h2 className="text-base font-semibold text-slate-800 mb-4">Planes disponibles</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
        {PLANS.map((plan) => {
          const isCurrent = plan.id === currentPlanId
          return (
            <div
              key={plan.id}
              className={`rounded-xl border-2 p-5 flex flex-col ${
                isCurrent
                  ? 'border-emerald-500 bg-emerald-50'
                  : 'border-slate-200 bg-white'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <h3 className="text-base font-bold text-slate-800">{plan.name}</h3>
                {isCurrent && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-emerald-600 text-white">
                    Plan actual
                  </span>
                )}
              </div>

              <div className="mb-4">
                <span className="text-2xl font-bold text-slate-900">{plan.price}</span>
                {plan.priceDetail && (
                  <span className="text-sm text-slate-500 ml-1">{plan.priceDetail}</span>
                )}
              </div>

              <ul className="space-y-1.5 mb-5 flex-1">
                <li className="text-sm text-slate-600 font-medium">{plan.credits}</li>
                <li className="text-sm text-slate-600">{plan.maxUsers}</li>
                {plan.features.map((f) => (
                  <li key={f} className="text-sm text-slate-600 flex items-start gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>

              {isAdmin && !isCurrent && !plan.isFree && plan.lookupKey && (
                <button
                  type="button"
                  onClick={() => handleUpgrade(plan.lookupKey!, plan.id)}
                  disabled={checkoutLoading !== null}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed w-full"
                >
                  {checkoutLoading === plan.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : null}
                  {checkoutLoading === plan.id ? 'Redirigiendo...' : `Actualizar a ${plan.name}`}
                </button>
              )}
              {isCurrent && (
                <span className="inline-flex items-center justify-center text-sm text-emerald-700 font-medium py-2">
                  Plan activo
                </span>
              )}
              {!isAdmin && !isCurrent && !plan.isFree && (
                <span className="inline-flex items-center justify-center text-xs text-slate-400 py-2">
                  Solo administradores pueden cambiar el plan
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
